import { Link, useNavigate } from 'react-router-dom';
import type { Site } from '@iot-dashboard/shared';
import { useDevices, useSites } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { Panel } from '../../components/Panel';
import { StatusDot, toneFor } from '../../components/StatusDot';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';

/** The console's firmware and the version of the app on it that runs the sensors and cameras. */
function softwareVersions(s: Site): string {
  const parts = [s.firmwareVersion && `OS ${s.firmwareVersion}`, s.protectVersion && `App ${s.protectVersion}`];
  return parts.filter(Boolean).join(' · ') || '—';
}

export function SitesPage() {
  const { tenantId, base } = useTenantContext();
  const navigate = useNavigate();
  const sites = useSites(tenantId);
  const devices = useDevices(tenantId);

  if (sites.isPending) return <Loading label="Loading sites…" />;
  if (sites.isError) return <ErrorState error={sites.error} retry={() => sites.refetch()} />;

  const countFor = (siteId: string, status?: string) =>
    (devices.data ?? []).filter((d) => d.siteId === siteId && (!status || d.status === status)).length;

  const columns: Column<Site>[] = [
    { key: 'status', header: 'Status', sortValue: (s) => s.status, render: (s) => <StatusDot status={s.status} /> },
    { key: 'name', header: 'Site', primary: true, sortValue: (s) => s.name, render: (s) => (
      <div>
        <div className="cell-primary">{s.name}</div>
        <div className="cell-sub">{s.location ?? s.consoleId}</div>
      </div>
    ) },
    { key: 'ip', header: 'IP address', render: (s) => <span className="mono">{s.ipAddress ?? '—'}</span> },
    { key: 'fw', header: 'Software', render: (s) => <span className="small">{softwareVersions(s)}</span> },
    { key: 'sensors', header: 'Sensors', align: 'right', sortValue: (s) => countFor(s.siteId), render: (s) => (
      <span className="row" style={{ justifyContent: 'flex-end', gap: 10 }}>
        {(['online', 'alerting', 'offline', 'dormant'] as const).map((st) => (
          <span key={st} className="status" title={st}>
            <span className={`dot dot--${toneFor(st)}`} aria-hidden="true" />
            <span className="tabular">{countFor(s.siteId, st)}</span>
          </span>
        ))}
      </span>
    ) },
    { key: 'sync', header: 'Last sync', sortValue: (s) => s.lastSyncAt ?? '', render: (s) => (
      <div>
        <TimeAgo iso={s.lastSyncAt} />
        {s.lastSyncError ? <div className="small" style={{ color: 'var(--critical-ink)' }}>{s.lastSyncError}</div> : null}
      </div>
    ) },
    { key: 'open', header: '', render: (s) => <Link className="btn btn--sm" to={`${base}/sensors?site=${encodeURIComponent(s.siteId)}`} onClick={(e) => e.stopPropagation()}>Sensors</Link> },
  ];

  return (
    <>
      <PageHeader title="Sites" subtitle="Each site is a console that manages this tenant's sensors and cameras at one location." />
      <Panel flush>
        <DataTable
          columns={columns}
          rows={sites.data}
          rowKey={(s) => s.siteId}
          onRowClick={(s) => navigate(`${base}/overview?site=${encodeURIComponent(s.siteId)}`)}
          defaultSort={{ key: 'name', dir: 'asc' }}
          empty={<div className="empty"><h3>No sites yet</h3><p>Sites appear after the first successful sync. <Link to={`${base}/integrations`}>Configure the integration</Link>.</p></div>}
        />
      </Panel>
    </>
  );
}
